import { CURRENT_CLIENT } from '@/constants'
import {
  YeshumanExpandMarkIcon,
  YeshumanMarkIcon,
} from '@/components/landing/yeshuman/yeshuman-brand-mark'
import { getSidebarIcon } from '@/components/sidebar/sidebar-icon-registry'
import { useDocumentDarkClass } from '@/hooks/use-document-dark-class'
import { useTenantLogoSrc } from '@/hooks/use-tenant-logo-src'
import {
  resolveChatAgentMarkSrc,
  type ClientMarkConfig,
} from '@/lib/client-mark-src'
import {
  getSidebarBrandConfig,
  sidebarBrandGlyphClass,
  sidebarBrandMarkClass,
} from '@/lib/sidebar-brand'
import { cn } from '@/lib/utils'

function SidebarBrandMarkImage({ className }: { className?: string }) {
  const brand = getSidebarBrandConfig()
  const glyphClass = sidebarBrandGlyphClass(brand.size)
  const cc = CURRENT_CLIENT as ClientMarkConfig & { brandIcon?: string }
  const isDark = useDocumentDarkClass()
  const logoSrc = useTenantLogoSrc({
    logoPath: cc.logoPath,
    logoPathLight: cc.logoPathLight,
    logoPathDark: cc.logoPathDark,
  })
  const markSrc = resolveChatAgentMarkSrc(
    {
      faviconPath: cc.faviconPath,
      faviconStyle: cc.faviconStyle,
      brandIcon: cc.brandIcon,
      theme: cc.theme,
    },
    logoSrc,
    isDark,
  )

  if (markSrc) {
    return (
      <img
        src={markSrc}
        alt=""
        className={cn(glyphClass, 'object-contain', className)}
        aria-hidden
      />
    )
  }

  const IconComponent = getSidebarIcon(cc.brandIcon || 'Bot')
  return (
    <IconComponent className={cn(glyphClass, 'text-foreground', className)} />
  )
}

type SidebarBrandMarkProps = {
  className?: string
  /** Collapsed hover: Yes Human circle+panel expand mark instead of stick figure. */
  expandHint?: boolean
}

/**
 * Sidebar rail brand: config-driven mark (yeshumanMark) or logo → favicon → Lucide.
 */
export function SidebarBrandMark({ className, expandHint = false }: SidebarBrandMarkProps) {
  const brand = getSidebarBrandConfig()

  if (brand.variant === 'yeshumanMark') {
    const markClass = cn(
      sidebarBrandMarkClass(brand.size),
      'shrink-0 text-foreground',
      className,
    )
    if (expandHint) {
      return <YeshumanExpandMarkIcon className={markClass} />
    }
    return <YeshumanMarkIcon className={markClass} />
  }

  return <SidebarBrandMarkImage className={className} />
}

/** True when collapsed hover should use the in-circle expand mark (not Lucide). */
export function sidebarBrandUsesExpandMark(): boolean {
  return getSidebarBrandConfig().variant === 'yeshumanMark'
}
